import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Paper,
  Alert,
  AlertTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import * as XLSX from "xlsx";

const CampaignImportData = ({ open, onClose, onFileSelected, campaign }) => {
  const [file, setFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [previewData, setPreviewData] = useState([]);
  const [importStatus, setImportStatus] = useState("initial"); // 'initial', 'uploading', 'preview', 'error'
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total: 0, valid: 0, invalid: 0 });
  const [visibleRows, setVisibleRows] = useState(50); // Número inicial de linhas visíveis
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const tableContainerRef = useRef(null);

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  const resetState = () => {
    setFile(null);
    setFileName("");
    setPreviewData([]);
    setImportStatus("initial");
    setError(null);
    setStats({ total: 0, valid: 0, invalid: 0 });
    setVisibleRows(50);
    setIsLoadingMore(false);
  };

  // Função para carregar mais linhas
  const loadMoreRows = useCallback(() => {
    if (isLoadingMore || visibleRows >= previewData.length) return;

    setIsLoadingMore(true);
    // Simular um pequeno delay para mostrar o loading
    setTimeout(() => {
      setVisibleRows((prev) => Math.min(prev + 50, previewData.length));
      setIsLoadingMore(false);
    }, 300);
  }, [visibleRows, previewData.length, isLoadingMore]);

  // Manipulador de scroll para detectar quando chegar no final
  const handleScroll = useCallback(
    (event) => {
      const { scrollTop, scrollHeight, clientHeight } = event.target;
      const threshold = 100; // Pixels antes do final para começar a carregar

      if (scrollHeight - scrollTop - clientHeight <= threshold) {
        loadMoreRows();
      }
    },
    [loadMoreRows]
  );

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setFileName(selectedFile.name);
      setImportStatus("uploading");

      // Processar o arquivo Excel
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: "array" });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          // Validar e transformar dados
          const processedData = validateAndTransformData(jsonData);
          setPreviewData(processedData);
          setImportStatus("preview");

          // Calcular estatísticas
          const validItems = processedData.filter((item) => item.isValid);
          setStats({
            total: processedData.length,
            valid: validItems.length,
            invalid: processedData.length - validItems.length,
          });
        } catch (err) {
          console.error("Erro ao processar arquivo:", err);
          setError(
            "Não foi possível processar o arquivo. Verifique o formato do arquivo e tente novamente."
          );
          setImportStatus("error");
        }
      };

      reader.onerror = () => {
        setError("Erro ao ler o arquivo.");
        setImportStatus("error");
      };

      reader.readAsArrayBuffer(selectedFile);
    }
  };

  const validateAndTransformData = (jsonData) => {
    return jsonData.map((row, index) => {
      const itemId =
        row["Item ID"] || row["item_id"] || row["id"] || row["ID"] || "";
      const dealPrice =
        row["Suggested Discounted Price"] ||
        row["deal_price"] ||
        row["preco"] ||
        row["preço"] ||
        row["price"] ||
        row["Price"] ||
        0;

      // Validar os dados
      const isValidItemId = !!itemId && typeof itemId !== "undefined";
      const isValidPrice =
        !isNaN(parseFloat(dealPrice)) && parseFloat(dealPrice) >= 0;

      let errors = [];
      if (!isValidItemId) {
        errors.push("Item ID obrigatório");
      }
      if (!isValidPrice) {
        errors.push("Preço inválido");
      }

      return {
        id: index,
        itemId: itemId?.toString().trim(),
        dealPrice: isValidPrice ? parseFloat(dealPrice) : null,
        isValid: isValidItemId && isValidPrice,
        errors: errors,
      };
    });
  };

  const handleImport = () => {
    if (previewData.length === 0) {
      setError("Nenhum dado para importar.");
      return;
    }

    // Filtrar apenas itens válidos
    const validItems = previewData.filter((item) => item.isValid);

    if (validItems.length === 0) {
      setError("Nenhum item válido para importar.");
      return;
    }

    // Enviar o arquivo para o componente pai
    if (file && onFileSelected) {
      onFileSelected(file, campaign);
    }
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={importStatus !== "uploading" ? onClose : undefined}
      maxWidth="md"
      fullWidth
      keepMounted
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <CloudUploadIcon sx={{ color: "#3483fa" }} />
        Importar Produtos para Campanha
        {campaign && (
          <Chip
            label={campaign.name}
            size="small"
            sx={{ ml: 1, bgcolor: "#e3f2fd", color: "#1976d2" }}
          />
        )}
      </DialogTitle>

      <DialogContent>
        {importStatus === "initial" && (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Importe uma planilha com produtos para adicionar à campanha. O
              formato esperado é uma tabela com as colunas:
            </Typography>

            <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
                Colunas obrigatórias:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                <li>
                  <strong>Item ID</strong> - ID do produto no Mercado Livre
                </li>
                <li>
                  <strong>Suggested Discounted Price</strong> - Preço
                  promocional sugerido
                </li>
              </ul>
            </Box>

            <Box sx={{ mt: 3, mb: 2 }}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{
                  borderColor: "#3483fa",
                  color: "#3483fa",
                  "&:hover": {
                    borderColor: "#2968c8",
                    bgcolor: "rgba(52, 131, 250, 0.1)",
                  },
                }}
              >
                Selecionar Arquivo Excel
                <input
                  type="file"
                  hidden
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                />
              </Button>
            </Box>
          </>
        )}

        {importStatus === "uploading" && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              my: 4,
            }}
          >
            <CircularProgress size={50} sx={{ color: "#3483fa" }} />
            <Typography variant="body1" sx={{ mt: 2 }}>
              Processando o arquivo...
            </Typography>
          </Box>
        )}

        {importStatus === "preview" && (
          <>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                Arquivo: {fileName}
              </Typography>

              <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                <Chip label={`Total: ${stats.total}`} variant="outlined" />
                <Chip
                  label={`Válidos: ${stats.valid}`}
                  color="success"
                  variant="outlined"
                />
                {stats.invalid > 0 && (
                  <Chip
                    label={`Inválidos: ${stats.invalid}`}
                    color="error"
                    variant="outlined"
                  />
                )}
              </Box>
            </Box>

            {stats.invalid > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>Atenção</AlertTitle>
                Há {stats.invalid} itens com dados inválidos que serão ignorados
                na importação.
              </Alert>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Prévia dos dados ({Math.min(visibleRows, previewData.length)} de{" "}
              {previewData.length} itens):
            </Typography>

            <TableContainer
              component={Paper}
              sx={{ maxHeight: 400, mb: 2 }}
              ref={tableContainerRef}
              onScroll={handleScroll}
            >
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Item ID</TableCell>
                    <TableCell align="right">Preço Promocional</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell>Erros</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {previewData.slice(0, visibleRows).map((row) => (
                    <TableRow
                      key={row.id}
                      sx={{
                        backgroundColor: row.isValid
                          ? "inherit"
                          : "rgba(255, 0, 0, 0.05)",
                        "&:nth-of-type(odd)": {
                          bgcolor: (theme) =>
                            row.isValid
                              ? theme.palette.action.hover
                              : "rgba(255, 0, 0, 0.1)",
                        },
                      }}
                    >
                      <TableCell sx={{ fontFamily: "monospace" }}>
                        {row.itemId || "—"}
                      </TableCell>
                      <TableCell align="right">
                        {row.dealPrice !== null
                          ? `R$ ${row.dealPrice.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`
                          : "—"}
                      </TableCell>
                      <TableCell align="center">
                        {row.isValid ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "success.main",
                            }}
                          >
                            <CheckCircleOutlineIcon
                              fontSize="small"
                              sx={{ mr: 0.5 }}
                            />
                            <Typography variant="body2">Válido</Typography>
                          </Box>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "error.main",
                            }}
                          >
                            <ErrorOutlineIcon
                              fontSize="small"
                              sx={{ mr: 0.5 }}
                            />
                            <Typography variant="body2">Inválido</Typography>
                          </Box>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.errors.length > 0 && (
                          <Typography variant="body2" color="error">
                            {row.errors.join(", ")}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Indicador de carregamento para mais linhas */}
                  {isLoadingMore && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 1,
                          }}
                        >
                          <CircularProgress size={20} />
                          <Typography variant="body2" color="text.secondary">
                            Carregando mais itens...
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}

                  {/* Indicador de final dos dados */}
                  {!isLoadingMore &&
                    visibleRows >= previewData.length &&
                    previewData.length > 50 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 1 }}>
                          <Typography variant="body2" color="text.secondary">
                            Todos os {previewData.length} itens foram carregados
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {importStatus === "error" && (
          <Alert severity="error" sx={{ my: 2 }}>
            <AlertTitle>Erro</AlertTitle>
            {error}
          </Alert>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        {importStatus === "initial" && (
          <Button onClick={onClose} sx={{ color: "text.secondary" }}>
            Cancelar
          </Button>
        )}

        {importStatus === "preview" && (
          <>
            <Button onClick={onClose} sx={{ color: "text.secondary" }}>
              Cancelar
            </Button>
            <Button onClick={resetState} sx={{ color: "text.secondary" }}>
              Selecionar outro arquivo
            </Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={stats.valid === 0}
              sx={{
                bgcolor: "#3483fa",
                color: "white",
                fontWeight: 600,
                "&:hover": { bgcolor: "#2968c8" },
              }}
            >
              Importar {stats.valid} produtos
            </Button>
          </>
        )}

        {importStatus === "error" && (
          <Button onClick={onClose} sx={{ color: "text.secondary" }}>
            Fechar
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CampaignImportData;
